import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { findBreadcrumb, NavGroup } from './navConfig';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePanelBreadcrumb } from './PanelBreadcrumbContext';

type TopbarProps = {
  groups: NavGroup[];
  onToggleSidebar?: () => void;
};

const Topbar: React.FC<TopbarProps> = ({ groups, onToggleSidebar }) => {
  const { pathname } = useLocation();
  const { trail } = usePanelBreadcrumb();
  const { user } = useAuth();
  const fallbackCrumbs = findBreadcrumb(groups, pathname);
  const crumbs = (trail && trail.length > 0 ? trail : fallbackCrumbs).filter(c => (c.label || '').trim());

  const breadcrumbNodes = [] as React.ReactNode[];
  for (let i = 0; i < crumbs.length; i++) {
    const c = crumbs[i];
    breadcrumbNodes.push(
      <BreadcrumbItem key={`item-${i}`}>
        {c.to ? (
          <BreadcrumbLink asChild>
            <Link to={c.to}>{c.label}</Link>
          </BreadcrumbLink>
        ) : (
          <BreadcrumbPage>{c.label}</BreadcrumbPage>
        )}
      </BreadcrumbItem>
    );
    if (i < crumbs.length - 1) {
      breadcrumbNodes.push(<BreadcrumbSeparator key={`sep-${i}`} />);
    }
  }

  const getUserName = () => {
    const meta = (user?.user_metadata as any) || {};
    return meta.name || meta.full_name || user?.email?.split('@')[0] || 'Usuario';
  };
  const getAvatar = () => {
    const meta = (user?.user_metadata as any) || {};
    return meta.picture || meta.avatar_url || '';
  };
  const handleLogout = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' } as any);
    } catch {}
    try { localStorage.removeItem('panel:auth'); } catch {}
    window.location.href = '/panel/login';
  };

  return (
    <div className="sticky top-0 z-40 panel-topbar">
      <div className="h-16 flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3">
          {/* Mobile sidebar toggle */}
          <button
            aria-label="Open sidebar"
            onClick={onToggleSidebar}
            className="md:hidden h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="hidden md:block">
            <Breadcrumb>
              <BreadcrumbList className="text-xs gap-0.5 md:gap-1">{breadcrumbNodes}</BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>

        {/* User menu */}
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-3 rounded-full px-2 py-1 hover:bg-white/30">
              <Avatar className="h-8 w-8">
                <AvatarImage src={getAvatar()} />
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {getUserName().slice(0,2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <span className="hidden sm:block text-sm font-medium text-slate-800">
                {getUserName()}
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                Salir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default Topbar;
